import { Add, DeleteForever } from "@mui/icons-material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import React, { useState } from "react";

import { CustomSwitch } from "components/common/CustomSwitch";
import { TextInput } from "components/common/TextInput";
import { Typography } from "new-components/typography/Typography";

export type AnnotationsPanelProps = {
  loading: boolean;
  updateAnnotationsFunc: (arg: string[], arg2: boolean) => void;
  enableAnnotations: boolean;
  annotations: string[];
  setAnnotations: (arr: string[]) => void;
  switchAnnotations: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setDeleteModalOpen: (arg: boolean) => void;
  setSelectedAnnotation: (arg: number) => void;
};

export function AnnotationsPanel(props: AnnotationsPanelProps) {
  const {
    enableAnnotations,
    annotations,
    setAnnotations,
    switchAnnotations,
    updateAnnotationsFunc,
    loading,
    setDeleteModalOpen,
    setSelectedAnnotation,
  } = props;

  const handleChangeAnnotation = (text: string, index: number) => {
    const newInputs = [...annotations];
    newInputs[index] = text;
    setAnnotations(newInputs);
  };

  const handleAddInput = () => {
    const filterEmptyInputs = annotations.filter((item) => item.trim().length !== 0);
    setAnnotations([...filterEmptyInputs, ""]);
    updateAnnotationsFunc([...filterEmptyInputs, ""], enableAnnotations);
  };

  const handleDeletion = (index: number) => {
    setSelectedAnnotation(index);
    setDeleteModalOpen(true);
  };

  return (
    <Box className="py-2 mb-2">
      <InputLabel className="uppercase text-sm text-neutral-700 font-bold break-words">Desired Annotations:</InputLabel>
      <Box className="border-b-2 border-solid border-neutral-200">
        <Box className="my-2 mx-2 flex justify-between items-center">
          <Typography>Request Annotations:</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <CustomSwitch
                  sx={{ mr: 2, ml: 1 }}
                  checked={enableAnnotations}
                  onChange={switchAnnotations}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={enableAnnotations ? "ON" : "OFF"}
              labelPlacement="start"
            />
          </FormGroup>
        </Box>
      </Box>
      {enableAnnotations && (
        <>
          <Box className="mt-3">
            {annotations.map((value, index) => (
              <AnnotationInputs
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                value={value}
                loading={loading}
                onDelete={() => handleDeletion(index)}
                onChange={(value) => handleChangeAnnotation(value, index)}
              />
            ))}
          </Box>

          <Box className="flex justify-between flex-col md:flex-row gap-2 border-b-2 border-solid pb-4 border-neutral-200">
            <Button
              disabled={loading}
              variant="contained"
              className="capitalize text-black tracking-normal shadow-none bg-[#E5E9ED]"
              endIcon={<Add />}
              onClick={handleAddInput}
            >
              Add Annotation Request
            </Button>

            <Button
              disabled={loading}
              variant="contained"
              className="capitalize text-white tracking-normal shadow-none ml-auto"
              endIcon={loading && <CircularProgress size={20} sx={{ color: "white" }} />}
              onClick={() => updateAnnotationsFunc(annotations, enableAnnotations)}
            >
              Save annotations
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

interface AnnotationInputsProps {
  value: string;
  loading: boolean;
  onDelete: () => void;
  onChange: (value: string) => void;
}

function AnnotationInputs(props: AnnotationInputsProps) {
  const { value, loading, onDelete, onChange } = props;

  const [typeAnnotation, setTypeAnnotation] = useState<string>("Box");

  // So far only Box is available so this functions has not much utility
  // its here when there are more type of annotations
  const handleBoxChange = (event: SelectChangeEvent) => {
    setTypeAnnotation(event.target.value as string);
  };

  return (
    <>
      <Box data-testid="annotation-box" className="flex justify-between items-center mb-3">
        <TextField
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder="Type desired label here"
          size="small"
          fullWidth
          disabled={loading}
        />
        <FormControl style={{ minWidth: 120, margin: "0 10px" }}>
          <InputLabel id="demo-simple-select-filled-label">Type</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={typeAnnotation}
            label="Age"
            onChange={handleBoxChange}
            defaultValue={typeAnnotation}
          >
            <MenuItem value="Box">Box</MenuItem>
          </Select>
        </FormControl>

        <IconButton disabled={loading || !value} onClick={onDelete} aria-label="back" className="p-2 ml-1">
          <DeleteForever className={value ? "text-black" : "text-grey"} />
        </IconButton>
      </Box>
      <Box className="flex mb-5">
        <IconButton aria-label="back" className="p-2 ml-1" disabled>
          <SubdirectoryArrowRightIcon className="text-grey" />
        </IconButton>
        <TextInput value={`Draw a Box around every ${value} in the image`} disabled fullWidth />
      </Box>
    </>
  );
}
